/* eslint-disable max-lines */
// 9fbef606107a605d69c0edbcd8029e5d
import get from 'lodash/get';
import logger from '@tcp/core/src/utils/loggerInstance';
import {
  getTopRatedAttributeToBeChecked,
  getBestSellerAttributeToBeChecked,
  getMostFavoritedAttributeToBeChecked,
} from '@tcp/core/src/reduxStore/selectors/session.selectors';
import {
  isBopisProduct,
  isBossProduct,
  attributeListMaker,
  getInfluencerTags,
  getInfluencerPicks,
} from './productParser';
import { extractExtraImages } from './productListing.utils';
import {
  extractAttributeValue,
  extractPrioritizedBadge,
  extractZoneOneBadges,
} from '../../../utils/badge.util';
import { isCanada, parseBoolean } from '../../../utils';
import processHelpers from './processHelpers';
import { PLP_ROUTE } from '../../../constants/plp.constants';

const getIsGiftCard = (isGiftCard, baseProduct) => {
  return isGiftCard ? 'Gift Card' : baseProduct?.product_name || '';
};

const isGiftCardItem = (product) =>
  !!(
    product &&
    product.style_partno &&
    (product.style_partno.toLowerCase() === 'giftcardbundle' || product.giftcard === '1')
  );

/* DTN-5579 BE  will be sending the list of badges to be excluded in any category
and we are checking if the particular category should show/hide a badge */
const getNavAttributes = (navTree, categoryId, attribute) => {
  const index = navTree ? navTree.length : 0;
  let iterator = 0;
  let categoryFound = '';
  while (iterator < index) {
    if (navTree[iterator]?.categoryId === categoryId) {
      categoryFound = navTree[iterator][attribute];
    } else if (navTree[iterator]?.menuItems?.length) {
      categoryFound = getNavAttributes(
        navTree[iterator].menuItems[0]?.length
          ? navTree[iterator].menuItems[0]
          : navTree[iterator].menuItems,
        categoryId,
        attribute
      );
    }
    if (categoryFound) {
      break;
    } else {
      iterator += 1;
    }
  }
  return categoryFound;
};

/**
 * @method homeBreadCrumbFactory -- give array to create Home breadcrumb
 * @param {undefined}
 * @return {array} breadCrumbs --contain home breadcrumb object in array
 */
const homeBreadCrumbFactory = () => {
  return [
    {
      displayName: 'Home',
      urlPathSuffix: 'home',
    },
  ];
};

/**
 * @function breadCrumbFactory -- create breadcrumb for PDP page
 * @param {Object} storeState --> contain all redux store data
 * @return {array} breadCrumbs --> number of obect item in array cotain breadcrumb info
 */
const breadCrumbFactory = (state) => {
  const plpBreadCrumb = state?.ProductListing?.breadCrumbTrail;
  return plpBreadCrumb || homeBreadCrumbFactory();
};

const routingInfoStoreView = {
  getOriginImgHostSetting: () => {
    return 'https://test4.childrensplace.com';
  },
};

const getSwatchImgPath = () => {
  return null;
};

const getProductImagePath = (id, excludeExtension, imageExtension) => {
  const imageName = (id && id.split('_')) || [];
  const imagePath = imageName[0];
  const extension = imageExtension ? `.${imageExtension}` : '.jpg';

  return {
    125: `${imagePath}/${id}${excludeExtension ? '' : extension}`,
    380: `${imagePath}/${id}${excludeExtension ? '' : extension}`,
    500: `${imagePath}/${id}${excludeExtension ? '' : extension}`,
    900: `${imagePath}/${id}${excludeExtension ? '' : extension}`,
  };
};

const getImgPath = (id, excludeExtension, imageExtension) => {
  return {
    colorSwatch: null,
    productImages: getProductImagePath(id, excludeExtension, imageExtension),
  };
};

function convertMultipleSizeSkusToAlternatives(sizes) {
  const uniqueSizesMap = Object.create(null);
  const result = [];

  if (sizes) {
    sizes.forEach((sizeVal, index) => {
      const size = sizeVal;
      let existingSizeForName = uniqueSizesMap[size.sizeName];
      let alternativeSkuIds;

      size.position = index;

      if (!existingSizeForName) {
        // Add current size to uniqueSizesMap if current size  not exist in uniqueSizesMap
        uniqueSizesMap[size.sizeName] = size;
        result.push(size);
      } else if (size.maxAvailable > existingSizeForName.maxAvailable) {
        alternativeSkuIds = existingSizeForName.skuId;
        uniqueSizesMap[size.sizeName] = size;
        result[existingSizeForName.position] = size;
        existingSizeForName = uniqueSizesMap[size.sizeName];
      } else {
        alternativeSkuIds = size.skuId;
        if (!existingSizeForName.alternativeSkuIds) {
          // Check if alternativeSkuIds already exist or not, if not, then intialize alternativeSkuIds with empty array
          existingSizeForName.alternativeSkuIds = [];
        }
        // store the skuId of duplicate size with less quantity as an alternative
        existingSizeForName.alternativeSkuIds.push(alternativeSkuIds);
        uniqueSizesMap[size.sizeName] = existingSizeForName;
        result[existingSizeForName.position] = existingSizeForName;
      }
    });
  }
  return result.filter((size) => size);
}

const sumBy = (arr, iteratee) => {
  const func = typeof iteratee === 'function' ? iteratee : (item) => item[iteratee];
  return arr?.reduce((acc, item) => acc + func(item), 0) || 0;
};

function getFirstVariant(product) {
  try {
    return product?.variants?.[0] || {};
  } catch (ex) {
    return {};
  }
}

const validateQuantityAvailable = () => {
  const index = 0;
  return index > -1 ? Number.MAX_VALUE : 0;
};

function getProductColorName(isGiftCard, product) {
  return isGiftCard
    ? product?.product_name || ''
    : product?.auxdescription || product?.TCPColor || product?.imagename || '';
}

const getTotalQtyAvailable = (sizes) => sumBy(sizes, 'v_qty');

const getTotalQtyAvailableBoss = (sizes) => sumBy(sizes, 'v_qty_boss');

const getSize = (sizeName) => {
  const size = sizeName?.split('_');
  return size?.length > 1 ? size[1] : sizeName;
};

const getFamilyName = (isGiftCard, itemColor) =>
  isGiftCard ? itemColor?.product_name || '' : itemColor?.TCPColor || '';

const getCategoryColorId = (itemColor) => {
  return itemColor?.categoryPath2_catMap && itemColor?.categoryPath3_catMap
    ? [...itemColor.categoryPath3_catMap, ...itemColor.categoryPath2_catMap]
    : itemColor?.categoryPath3_catMap || itemColor?.categoryPath2_catMap;
};

const getCategoryEntity = (categoryColorId, breadCrumbs) => {
  return categoryColorId && processHelpers.parseCategoryEntity(categoryColorId, breadCrumbs);
};

const getImagePathAttr = (isGiftCard) => (isGiftCard ? 'prodpartno' : 'imagename');

const getImagesByColor = (itemColor, colorName, getImgPathFunc, isGiftCard, imagesByColor) => {
  const imageNameAttr = getImagePathAttr(isGiftCard);
  const imageExtension = itemColor?.productimage || '';
  return {
    ...extractExtraImages(
      `${itemColor[imageNameAttr]}#${colorName}`,
      itemColor?.alt_img,
      getImgPathFunc,
      false,
      false,
      isGiftCard,
      false,
      imageExtension
    ),
    ...imagesByColor,
  };
};

const getPdpUrl = (isBundleProduct, itemColor) => {
  return isBundleProduct
    ? `/b/${itemColor?.seo_token || itemColor?.uniqueId}`
    : `/p/${itemColor?.uniqueId}`;
};

const getProductName = (isBundleProduct, itemColor) =>
  isBundleProduct ? itemColor?.product_name || '' : null;

const getMaxAvailable = (itemColor) => {
  return getTotalQtyAvailable(itemColor?.variants) || 0;
};

const getMaxAvailableBoss = (itemColor) => {
  return getTotalQtyAvailableBoss(itemColor?.variants) || 0;
};

const setPriceRange = (product) => {
  return {
    listPrice: parseFloat(product?.min_list_price) || parseFloat(product?.min_offer_price) || 0,
    offerPrice: parseFloat(product?.min_offer_price) || 0,
    highListPrice: parseFloat(product?.high_list_price) || 0,
    highOfferPrice: parseFloat(product?.high_offer_price) || 0,
    lowListPrice: parseFloat(product?.low_list_price) || 0,
    lowOfferPrice: parseFloat(product?.low_offer_price) || 0,
  };
};

const getIsVMPProduct = (styleTyp) => {
  return styleTyp === '0002' || styleTyp === '0003' || styleTyp === '0005' || styleTyp === '0006';
};

const getVariantVisePreOrder = (variant, dateString) => {
  const preOrderDates = dateString?.split('|');
  let variantVisePreOrder = preOrderDates?.find((string) => {
    const variantId = string?.split('#')[0];
    return variantId === variant?.variantId;
  });
  if (variantVisePreOrder) {
    variantVisePreOrder = variantVisePreOrder?.split('#');
    return {
      variant: variant?.variantId,
      preOrderDate: variantVisePreOrder[1],
      size: variant?.v_tcpsize?.split('_')[1],
    };
  }
  return {};
};

const getVariantsMap = (currentProduct, productAttributes) => {
  if (!currentProduct?.variants?.length) return null;
  return currentProduct?.variants.map((variant) => {
    return getVariantVisePreOrder(
      variant,
      extractAttributeValue(currentProduct, productAttributes?.variantPreOrderDate)
    );
  });
};

/**
 * Function to get TCP Fit Message based on the location.
 * @param {Object} baseProduct - The base product object containing attributes.
 * @returns {Array} - Filtered array of attributes based on location.
 */
const getTcpFitMessageStore = (baseProduct) => {
  // Create attributes array using attributeListMaker
  const attributesArray = attributeListMaker(baseProduct?.list_of_attributes || '') || [];

  // Filter attributes based on the location
  return attributesArray.filter((attribute) => attribute.identifier === 'TCPFitAssociation');
};

const getNewFitList = (baseProduct) => {
  const tcpFitMsgStore = getTcpFitMessageStore(baseProduct);
  return tcpFitMsgStore[0]?.value
    ?.split(',')
    ?.map((item) => {
      const parts = item.split('#');
      const type = parts.length > 1 ? parts[1].toLowerCase() : null;
      // Return an object if type is valid
      return type ? { displayName: type, id: type } : null;
    })
    .filter((obj) => obj);
};

const getFitProductMap = (baseProduct) => {
  const tcpFitMsgStore = getTcpFitMessageStore(baseProduct);
  // Create the mapping object
  const fitProductMap = {};
  tcpFitMsgStore[0]?.value?.split(',')?.forEach((item) => {
    const parts = item?.split('#');
    const id = parts[0]; // Get the ID
    const type = parts.length > 1 ? parts[1]?.toLowerCase() : null; // Get the type and convert to lowercase
    // Add to the map if type is valid
    if (type) {
      fitProductMap[type] = id; // Map type to ID
    }
  });
  return fitProductMap; // Return the constructed object
};

const checkNewFitAttribute = (baseProduct) => {
  const tcpFitMsgStore = getTcpFitMessageStore(baseProduct);
  return tcpFitMsgStore?.length > 0;
};

/**
 * Function to find the key name in an object where the value matches the given pid.
 * @param {Object} fitProductMap - The object containing product mappings.
 * @param {string} pid - The product ID to search for.
 * @returns {string|null} - The key name if found, otherwise null.
 */
const getFitNameFromProductId = (fitProductMap, pid) => {
  if (
    !fitProductMap ||
    typeof fitProductMap !== 'object' ||
    Object.keys(fitProductMap).length === 0
  ) {
    return null;
  }
  const entry = Object.entries(fitProductMap).find(([, value]) => value === pid);
  return entry ? entry[0] : null;
};

const getColorfitsSizesMap = ({
  productVariants,
  isGiftCard,
  breadCrumbs,
  // eslint-disable-next-line
  getImgPathFunc,
  images,
  hasFit,
  isBundleProduct,
  colorsFitsMap,
  excludeBage,
  productAttributes,
  state,
}) => {
  let imagesByColor = images;
  const isUSStore = !isCanada();
  const itemColorMap = productVariants.map((itemColor) => {
    const imageNameAttr = getImagePathAttr(isGiftCard); // A quickfix for changing images in swatches for giftcard
    const { productImages, colorSwatch } = getImgPath(itemColor[imageNameAttr]);
    let colorName = getProductColorName(isGiftCard, itemColor);
    const familyName = getFamilyName(isGiftCard, itemColor);
    const categoryColorId = getCategoryColorId(itemColor);
    const categoryEntity = getCategoryEntity(categoryColorId, breadCrumbs);
    const productStyleType = extractAttributeValue(itemColor, productAttributes?.tcpStyle);
    const isVMPProduct = getIsVMPProduct(productStyleType);
    const bossDisabledFlags = {
      bossProductDisabled:
        extractAttributeValue(
          itemColor,
          processHelpers.getProductAttributes()?.bossProductDisabled
        ) || 0,
      bossCategoryDisabled:
        extractAttributeValue(
          itemColor,
          processHelpers.getProductAttributes()?.bossCategoryDisabled
        ) || 0,
    };

    if (imagesByColor && colorName in imagesByColor) {
      colorName = `${colorName}-${itemColor?.imagename}`;
    }

    imagesByColor = getImagesByColor(itemColor, colorName, getImgPath, isGiftCard, imagesByColor);
    const groupLabel = extractAttributeValue(itemColor, productAttributes?.bundleGrouping);
    const newFitList = getNewFitList(itemColor);
    const newFitProductMap = getFitProductMap(itemColor);
    const hasNewFitAttribute = checkNewFitAttribute(itemColor);

    return {
      color: {
        name: colorName,
        imagePath: isGiftCard ? productImages[125] : colorSwatch,
        family: familyName,
        swatchImage: itemColor?.swatchimage,
        // Family name can be different from color name, quickViewStoreView using family name to find the initial value of Quick View Form
      },
      pdpUrl: getPdpUrl(isBundleProduct, itemColor),
      pdpSeoUrl: itemColor?.seo_token,
      name: getProductName(isBundleProduct, itemColor),
      colorProductId: itemColor?.productid,
      colorDisplayId: itemColor?.uniqueId,
      categoryEntity,
      imageName: itemColor?.imagename,
      favoritedCount: itemColor?.favoritedcount,
      maxAvailable: getMaxAvailable(itemColor),
      maxAvailableBoss: getMaxAvailableBoss(itemColor),
      hasFits: hasFit,
      hardMarkDown: itemColor?.is_hard_mark_enable,
      TCPPromotionID: itemColor?.tcp_promotion_id,
      TCPPromotionPrice: itemColor?.tcp_promo_price,
      isVMPEnabled: itemColor?.isVMPEnabled,
      miscInfo: {
        bossDisabledFlags,
        isBopisEligible: isBopisProduct(isUSStore, itemColor) && !isGiftCardItem(itemColor),
        isBossEligible: isBossProduct(bossDisabledFlags) && !isGiftCardItem(itemColor),
        badge1: extractPrioritizedBadge(itemColor, productAttributes, '', excludeBage, true),
        badge2: extractAttributeValue(itemColor, productAttributes?.merchant),
        zoneOneBadges: extractZoneOneBadges(itemColor, productAttributes),
        topRatedSLP: extractAttributeValue(itemColor, getTopRatedAttributeToBeChecked(state)),
        bestSellerSLP: extractAttributeValue(itemColor, getBestSellerAttributeToBeChecked(state)),
        mostFavoritedSLP: extractAttributeValue(
          itemColor,
          getMostFavoritedAttributeToBeChecked(state)
        ),
        isClearance: extractAttributeValue(
          itemColor,
          processHelpers.getProductAttributes()?.clearance
        ),
        hasOnModelAltImages: extractAttributeValue(
          itemColor,
          processHelpers.getProductAttributes()?.onModelAltImages
        ),
        videoUrl: extractAttributeValue(itemColor, productAttributes?.videoUrl),
        keepAlive: parseBoolean(itemColor?.[productAttributes?.keepAlive]),
        isInDefaultWishlist: processHelpers.addingExtraProductInfo(itemColor, state, true),
        groupBadgeLabel: groupLabel,
        isOnline: extractAttributeValue(itemColor, productAttributes?.onlineOnly),
        tcpStyleType: extractAttributeValue(itemColor, productAttributes?.tcpStyle),
        tcpStyleQty: extractAttributeValue(itemColor, productAttributes?.tcpStyleQty),
        mprLoyaltyMultiplier: extractAttributeValue(itemColor, productAttributes?.mprLoyaltyMul),
        plccLoyaltyMultiplier: extractAttributeValue(itemColor, productAttributes?.plccLoyaltyMul),
        preOrderDate: extractAttributeValue(itemColor, productAttributes?.preOrderDate),
        // Here we are extracting preOrderDate from the response.
        variantPreOrderMap: getVariantsMap(itemColor, productAttributes),
        isVMPProduct,
      },
      influencerTags: getInfluencerTags(itemColor),
      influencerPicks: getInfluencerPicks(itemColor),
      fits: colorsFitsMap[colorName],
      fewSizesLeft: itemColor?.fewSizesLeft || false,
      newFitList,
      newFitProductMap,
      hasNewFitAttribute,
      ...setPriceRange(itemColor),
      // TODO - fix this - unbxdId: getUnbxdId(),
      didPreventClick:
        itemColor?.color_consolidated_product && !itemColor?.isColorConsolidatedProductsFetch,
      storeSpecials: parseBoolean(itemColor?.store_specials),
      isLicensed: parseBoolean(extractAttributeValue(itemColor, 'isLicensed')) || false,
      bigIdeaLicensedProductBrand:
        extractAttributeValue(itemColor, 'TCPBigIdeaLicensedProductBrand') || false,
    };
  });
  return {
    itemColorMap,
    imagesByColor,
  };
};

const isNumberCheck = (number) => typeof number === 'number' && !Number.isNaN(number);

/** @function getDeepCopy
 * @summary This function will return deepCopy of a given value.
 */
export const getDeepCopy = (value) => {
  if (!value) {
    return value;
  }

  try {
    return structuredClone(value);
  } catch (err) {
    logger.error({
      error: err,
      extraData: {
        title: 'An error occurred while creating deep copy from value',
        componentName: 'ProductDetail.utils.js',
        methodName: 'getDeepCopy',
        value,
      },
    });
    return value;
  }
};

/** @function getUpdatedSizesBasedOnInventory
 * @param {array} sizes - Array of sizes for a particular productPartNo, this will usually come from colorFitsSizesMap => fits => sizes
 * @param {object} inventoryData - InventoryData Object from SFCC Inventory api, contains variant information for particular productPartNo
 * @returns {array} new array of sizes with updated maxAvailable data based on inventoryData
 * @summary This function will take sizes (also known as variants ), which would mostly come from colorFitsSizes map for a particular productPartNo > fits > sizes, and data from SFCC Inventory API. It'll then find the variantId in SFCC Inventory data, and if found will change the maxAvailable of size/variant based on 3 scenarios =>
 *  1. If inventoryData returns orderable as true & max available for size is 0, then maxAvailable will be set to 15
 *  2. If inventoryData returns orderable as false, then maxAvailable will be set to 0 for disabling the size.
 *  3. Else it'll return the current maxAvailable size.
 */

const getUpdatedSizesBasedOnInventory = ({
  sizes,
  inventoryData,
  shouldUpdateInventoryAttributesCheck,
  sfccSocialProofingOn,
}) => {
  return sizes.map((size) => {
    const newSize = { ...size };
    const foundVariant = inventoryData?.variants?.find(
      (variant) => variant.product_id === size.variantId
    );
    if (foundVariant) {
      if (shouldUpdateInventoryAttributesCheck && foundVariant.price) {
        newSize.offerPrice = foundVariant.price;
      }
      if (foundVariant.orderable && size.maxAvailable === 0) {
        newSize.maxAvailable = 15;
      }
      if (!foundVariant.orderable) {
        newSize.maxAvailable = 0;
      }
    }

    if (sfccSocialProofingOn) {
      const customVariant = inventoryData?.c_customVariants?.[size.variantId];
      if (customVariant && customVariant.ecomOrderableQty) {
        const { ecomOrderableQty } = customVariant;
        newSize.socialProofingQty = ecomOrderableQty;
      }
    }
    return newSize;
  });
};

/** @function getUpdatedInventoryVQTYForRawData
 * @param {array} sizes - Array of sizes for a particular productPartNo, this will usually come from colorFitsSizesMap => fits => sizes
 * @param {object} inventoryData - InventoryData Object from SFCC Inventory api, contains variant information for particular productPartNo
 * @returns {array} new array of sizes with updated v_qty data based on inventoryData
 * @summary This function will take sizes (also known as variants ), which would mostly come from colorFitsSizes map for a particular productPartNo > fits > sizes, and data from SFCC Inventory API. It'll then find the variantId in SFCC Inventory data, and if found will change the maxAvailable of size/variant based on 3 scenarios =>
 *  1. If inventoryData returns orderable as true & max available for size is 0, then v_qty will be set to 15
 *  2. If inventoryData returns orderable as false, then v_qty will be set to 0 for disabling the size.
 *  3. Else it'll return the current v_qty size.
 */

const getUpdatedInventoryVQTYForRawData = (variantCurrent, inventoryData) => {
  const newSize = { ...variantCurrent };
  const foundVariant = inventoryData?.variants?.find(
    (variant) => variant.product_id === variantCurrent?.variantId
  );
  if (foundVariant) {
    if (foundVariant.orderable && variantCurrent.v_qty === 0) {
      newSize.v_qty = 15;
    }
    if (!foundVariant.orderable) {
      newSize.v_qty = 0;
    }
  }
  return newSize?.v_qty;
};

/** @function updateColorFitsSizesMapWithSFCCInventoryData
 * @param {array} colorFitsSizesMap - colorFitsSizesMap array from productInfo.
 * @param {array} data - Array of data returned from SFCC Inventory API.
 * @returns {array} new colorFitsSizesMap with maxAvailable value updated based on data returned from SFCC inventory API
 */

// eslint-disable-next-line sonarjs/cognitive-complexity
export const updateColorFitsSizesMapWithSFCCInventoryData = ({
  colorFitsSizesMap,
  data,
  shouldUpdateInventoryAttributesCheck,
  defaultStoreId,
  storeSpecialV2Enabled,
  storeSpecialPREnabled,
  sfccSocialProofingOn,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  if (!colorFitsSizesMap) {
    return colorFitsSizesMap;
  }
  try {
    const currentColorFitsSizesMap = JSON.parse(JSON.stringify(colorFitsSizesMap));
    currentColorFitsSizesMap.map((item) => {
      const currentColorProductId = item?.colorProductId;
      // Looping over the SFCC Inventory Data
      for (let i = 0; i < data.length; i += 1) {
        if (data[i]?.id === currentColorProductId) {
          const { fits } = item;
          let maxAvailable = 0;
          // Looping over the fits to get the sizes(variants) for the matched productPartNo
          for (let j = 0; j < fits.length; j += 1) {
            const { sizes } = fits[j] || {};
            const newSizes = getUpdatedSizesBasedOnInventory({
              sizes,
              inventoryData: data[i],
              shouldUpdateInventoryAttributesCheck,
              sfccSocialProofingOn,
            });
            fits[j].sizes = newSizes;
            maxAvailable += sumBy(newSizes, 'maxAvailable');
          }
          if (isNumberCheck(maxAvailable)) {
            // eslint-disable-next-line no-param-reassign
            item.maxAvailable = maxAvailable;
          }
          if (shouldUpdateInventoryAttributesCheck) {
            if (data[i].c_TCPPromotionId) {
              // eslint-disable-next-line no-param-reassign
              item.TCPPromotionID = data[i].c_TCPPromotionId;
            }
            if (data[i].c_TCPPromoPrice) {
              // eslint-disable-next-line no-param-reassign
              item.TCPPromotionPrice = data[i].c_TCPPromoPrice;
            }
            const badge2 = isCanada()
              ? data[i].c_TCPMerchantTagCanadaStore
              : data[i].c_TCPMerchantTagUSStore;
            if (badge2 && item.miscInfo) {
              // eslint-disable-next-line no-param-reassign
              item.miscInfo.badge2 = badge2;
            }
            if (data[i].price) {
              // eslint-disable-next-line no-param-reassign
              item.offerPrice = data[i].price;
            }
            if (data[i].price_max) {
              // eslint-disable-next-line no-param-reassign
              item.highOfferPrice = data[i].price_max;
            }
            if (storeSpecialV2Enabled && data[i].c_storeSpecialDetails) {
              // eslint-disable-next-line no-param-reassign
              item.minStorePrice = data[i].c_storeSpecialDetails[defaultStoreId]?.minStorePrice;
              // eslint-disable-next-line no-param-reassign
              item.maxStorePrice = storeSpecialPREnabled
                ? data[i].c_storeSpecialDetails[defaultStoreId]?.maxStorePrice
                : data[i].c_storeSpecialDetails[defaultStoreId]?.minStorePrice;
              // eslint-disable-next-line no-param-reassign
              item.storeId = defaultStoreId;
            }
          }
          break;
        }
      }
      return null;
    });
    return currentColorFitsSizesMap;
  } catch (err) {
    logger.error({
      error: err,
      extraData: {
        title: 'An error occurred while getting updated colorFitsSizesMap',
        componentName: 'ProductDetail.utils',
        methodName: 'updateColorFitsSizesMapWithSFCCInventoryData',
        colorFitsSizesMap,
        data,
      },
    });
    return colorFitsSizesMap;
  }
};

const parseCategoryId = (pathMap, breadCrumbs) => {
  const L2Category = breadCrumbs?.slice(0, 2) || [];
  const breadCategory =
    L2Category.length > 1 ? L2Category.map((value) => value.categoryId).join('>') : '';
  let categoryName;

  if (L2Category.length <= 1) {
    const categoryEntity = processHelpers.getParticularCategory(pathMap, L2Category);
    const entities = categoryEntity?.split('|');
    categoryName = entities?.[0]?.split('>');
  }

  return categoryName ? categoryName.join('>') : breadCategory;
};

/**
 * isPureVMPProduct
 * @param {*} styleType 0005,0006 is pure vmp product
 */
export const isPureVMPProduct = (styleType) => {
  return styleType === '0005' || styleType === '0006';
};

/** @function updateRawDataWithSFCCInventoryData
 * @param {array} arrayToBeUpdatedWithInventory - arrayToBeUpdatedWithInventory.
 * @param {object} data - Data returned from SFCC Inventory API.
 * @param {object} variation1 - arrayToBeUpdatedWithInventory with slightly different form of structure for newCategoryIdList
 * @param {object} variation2 -  arrayToBeUpdatedWithInventory with slightly different form of structure for categoryIdList
 * @returns {array} new map with v_qty value updated based on data returned from SFCC inventory API
 */

export const updateRawDataWithSFCCInventoryData = (
  arrayToBeUpdatedWithInventory,
  data,
  variation1 = false,
  variation2 = false
  // eslint-disable-next-line sonarjs/cognitive-complexity
) => {
  try {
    if (!arrayToBeUpdatedWithInventory) {
      return arrayToBeUpdatedWithInventory;
    }
    const currentArrayToBeUpdatedWithInventory = JSON.parse(
      JSON.stringify(arrayToBeUpdatedWithInventory)
    );
    if (variation2) {
      const currentProdPartNo = currentArrayToBeUpdatedWithInventory?.[0]?.prodpartno;
      let currentStyleIndex = 0;
      for (let i = 0; i < data.length; i += 1) {
        if (data[i]?.id === currentProdPartNo) {
          currentStyleIndex = i;
        }
      }
      for (let j = 0; j < currentArrayToBeUpdatedWithInventory.length; j += 1) {
        if (data[currentStyleIndex]?.id === currentProdPartNo) {
          const variantCurrent = currentArrayToBeUpdatedWithInventory?.[j] || {};
          const updatedVQty = getUpdatedInventoryVQTYForRawData(
            variantCurrent,
            data?.[currentStyleIndex]
          );
          currentArrayToBeUpdatedWithInventory[j].v_qty = updatedVQty;
        }
      }
    } else {
      currentArrayToBeUpdatedWithInventory.map((item) => {
        const currentProdPartNo = variation1 ? item?.[0]?.prodpartno : item?.prodpartno;
        for (let i = 0; i < data.length; i += 1) {
          if (data[i]?.id === currentProdPartNo) {
            let { variants } = item || {};
            if (variation1) {
              variants = item;
            }
            for (let j = 0; j < variants.length; j += 1) {
              const variantCurrent = variants?.[j] || {};
              const updatedVQty = getUpdatedInventoryVQTYForRawData(variantCurrent, data?.[i]);
              variants[j].v_qty = updatedVQty;
            }
            break;
          }
        }
        return null;
      });
    }
    return currentArrayToBeUpdatedWithInventory;
  } catch (err) {
    logger.error({
      error: err,
      extraData: {
        title: 'An error occurred while getting updated arrayToBeUpdatedWithInventory',
        componentName: 'ProductDetail.utils',
        methodName: 'updateRawDataWithSFCCInventoryData',
        arrayToBeUpdatedWithInventory,
        data,
      },
    });
    return arrayToBeUpdatedWithInventory;
  }
};

const getCurrentProduct = (product, colorIdOrSeoKeyword) => {
  if (product?.length) {
    return product.find((prod) => prod.uniqueId === colorIdOrSeoKeyword);
  }
  return product;
};

const getReviewsCountProduct = (product, colorIdOrSeoKeyword) => {
  const currentProduct = getCurrentProduct(product, colorIdOrSeoKeyword);
  return {
    reviewsCount: parseInt(currentProduct?.TCPBazaarVoiceReviewCount, 10) || 0,
    ratings: currentProduct?.TCPBazaarVoiceRating || 0,
  };
};

const getCategoryPathMap = (baseProduct) => {
  return baseProduct?.categoryPath3_catMap || baseProduct?.categoryPath2_catMap;
};

const getCategoryPath2Map = (baseProduct) => {
  return baseProduct?.categoryPath2_catMap;
};

const getCategory = (baseProduct) => {
  return baseProduct?.categoryPath2_catMap?.[0]?.split('|')[0] || '';
};

const getCategoryId = (breadCrumbs, baseProduct, categoryPath) => {
  return breadCrumbs?.length && breadCrumbs[0]?.categoryId
    ? parseCategoryId(baseProduct?.categoryPath2_catMap, breadCrumbs)
    : categoryPath;
};

const getCategoryValue = (baseProduct) => {
  const attributesArray = attributeListMaker(baseProduct?.list_of_attributes || '') || [];
  const primeCategoryArray = attributesArray.filter(
    (attribute) => attribute.identifier === processHelpers.getProductAttributes()?.primaryCategory
  );
  return get(primeCategoryArray, '[0].value', '');
};

const getBaseProduct = (product) => {
  return product?.[0] || product;
};

const getProductVariants = (product) => {
  return (Array.isArray(product) ? product : Array.of(product)) || [];
};

const getFitName = (sizeVariant) => {
  return sizeVariant?.v_tcpfit?.toLowerCase() || '';
};

const getInventoryStatus = (sizeVariant) => {
  return sizeVariant?.v_qty && sizeVariant.v_qty !== 0 && sizeVariant.v_qty !== '';
};

const getSizeName = (sizeVariant) => {
  return getSize(sizeVariant?.v_tcpsize) || getSize(sizeVariant?.style_name);
};

const getClothingSize = (sizeVariant) => {
  return sizeVariant?.v_clothingsize || '';
};

const getShoesSize = (sizeVariant) => {
  return sizeVariant?.v_shoessize || '';
};

const getAccessoriesSize = (sizeVariant) => {
  return sizeVariant?.v_accessoriessize || '';
};

const getListPriceProduct = (sizeVariant) => {
  return parseFloat(sizeVariant?.v_listprice) || 0;
};

const getOfferPriceProduct = (sizeVariant) => {
  return parseFloat(sizeVariant?.v_offerprice) || 0;
};

const parseObjWithSameKey = (jsonString) => {
  const trimmedString = jsonString.replace(/^{(.*)}$/, '$1');
  const keyValuePairs = trimmedString.split(',');

  const resultObject = {};

  keyValuePairs.forEach((pair, index) => {
    const [key, value] = pair.split(':');
    const keyWithIndex = `${key.replace(/"/g, '').trim()}_${index}`;
    resultObject[keyWithIndex] = value.trim().replace(/"/g, '');
  });

  return resultObject;
};

const getAlternateSizes = (defaultColorAlternateSizes, otherColorAlternateSizes) => {
  return defaultColorAlternateSizes || otherColorAlternateSizes
    ? parseObjWithSameKey(defaultColorAlternateSizes || otherColorAlternateSizes)
    : {};
};

const getSortedKeys = (currentColorFitsSizesMap, sortOptions) => {
  return Object.keys(currentColorFitsSizesMap).sort(
    (a, b) =>
      (sortOptions[a.toLowerCase()] || sortOptions.other) -
      (sortOptions[b.toLowerCase()] || sortOptions.other)
  );
};

const getHasDefaultFit = (hasDefaultFit, isDefaultFit) => {
  return hasDefaultFit || isDefaultFit;
};

const isColorVariant = (colorVariant) => {
  return colorVariant && colorVariant.variants;
};

const setDefault = (hasDefaultFit, colorsFitsMap, color) => {
  return !hasDefaultFit && colorsFitsMap[color]?.length;
};

const getDefaultColorAlternateSizes = (colorVariant, colorIdOrSeoKeyword) => {
  return colorVariant?.uniqueId === colorIdOrSeoKeyword && colorVariant?.additional_styles;
};

const getIsAdditionalStyles = (hasAdditionalStyles, colorVariant) => {
  return !hasAdditionalStyles && colorVariant?.additional_styles;
};

const getSortedl2Group = (l1SubCategories = {}) => {
  const level2Groups = Object.values(l1SubCategories);
  return level2Groups.sort((prevGroup, curGroup) => {
    return prevGroup.order - curGroup.order;
  });
};

// eslint-disable-next-line sonarjs/cognitive-complexity
const getCurrentNavCat = (navData, primaryCategoryID) => {
  const categoryFound = [];
  if (!Array.isArray(navData) || !primaryCategoryID) return categoryFound;
  try {
    navData.some((l1) => {
      if (l1?.id === primaryCategoryID) {
        const subCategories = [];
        const l2groupSorted = getSortedl2Group(l1?.subCategories);
        l2groupSorted.forEach((l2group) => {
          l2group.items.forEach((subCat) => {
            if (subCat?.categoryContent?.displayToCustomer) {
              subCategories.push({
                asPath: subCat.asPath,
                categoryContent: subCat.categoryContent,
              });
            }
          });
        });
        categoryFound.push({ ...l1?.categoryContent, catDepth: 'l1', subCategories });
        return true;
      }
      const l2groupSort = getSortedl2Group(l1?.subCategories) || [];
      const l1CatFound = l2groupSort.some((l2group) => {
        return (
          Array.isArray(l2group?.items) &&
          l2group.items.some((l2) => {
            if (l2?.categoryContent?.id === primaryCategoryID) {
              categoryFound.push({
                ...l2.categoryContent,
                subCategories: l2.subCategories,
                catDepth: 'l2',
                hasL3: l2.hasL3 > 0,
                url: l2?.url,
              });
              return true;
            }
            const l2CatFound =
              Array.isArray(l2?.subCategories) &&
              l2.subCategories.some((l3) => {
                if (l3?.categoryContent?.id === primaryCategoryID) {
                  categoryFound.push({ ...l3.categoryContent, catDepth: 'l3' });
                  return true;
                }
                return false;
              });
            if (l2CatFound) {
              categoryFound.unshift({ ...l2.categoryContent, url: l2?.url, catDepth: 'l2' });
            }
            return l2CatFound;
          })
        );
      });
      if (l1CatFound) {
        categoryFound.unshift({ ...l1?.categoryContent, catDepth: 'l1' });
      }
      return l1CatFound;
    });
  } catch (e) {
    logger.error({
      error: `Error in Categorylistingpage selectors - ${e}`,
      extraData: {
        componentName: 'getCurrentNavCat',
        categoryId: primaryCategoryID,
      },
    });
  }
  return categoryFound;
};

const extractCategory = (category) => {
  try {
    let categoryId;
    if (Number.isInteger(category)) {
      categoryId = category;
    } else if (category?.length) {
      categoryId = category.split('/').pop();
    }
    return categoryId;
  } catch (error) {
    logger.error({
      error,
      extraData: {
        component: 'ProductDetail Util - extractCategory',
        categoryRecieved: category,
      },
    });
    return category;
  }
};

const processBreadcrumbs = (breadcrumbs) => {
  if (breadcrumbs?.length) {
    return breadcrumbs.map((crumb) => ({
      displayName: crumb?.name,
      destination: PLP_ROUTE,
      pathSuffix: extractCategory(crumb?.url),
    }));
  }
  return [];
};

const getAlternateStyleBasedOnSelectedColor = (allProductId = [], pickedProdcut) => {
  const product = allProductId.find((obj) => obj?.prodpartno === pickedProdcut);
  return getAlternateSizes(product?.additional_styles);
};

const getSeoDataForProductVariants = (variants, isGiftCard, baseProduct) => {
  const brandAttribute = isCanada() ? 'TCPPrimaryBrandCanadaStore' : 'TCPPrimaryBrandUSStore';
  if (Array.isArray(variants) && variants.length) {
    return variants
      ?.filter((variant) => variant?.productid !== baseProduct?.productid)
      ?.map((variant) => ({
        name: `${variant?.product_name}`,
        description: variant?.product_long_description,
        sku: variant?.productid,
        color: getProductColorName(isGiftCard, variant),
        image: `${variant?.style_partno}/${variant?.productimage}`,
        brand: extractAttributeValue(variant, brandAttribute),
        price: parseFloat(variant?.min_offer_price) || 0,
        promoPrice: parseFloat(variant?.tcp_promo_price) || 0,
        listPrice: parseFloat(variant?.min_list_price) || 0,
        ratingValue: variant?.TCPBazaarVoiceRating || '4.5',
        reviewCount: variant?.TCPBazaarVoiceReviewCount || '120',
        availability: extractAttributeValue(variant, 'totalInventory'),
      }));
  }
  return [];
};

const processHelperUtil = {
  getIsGiftCard,
  getNavAttributes,
  homeBreadCrumbFactory,
  breadCrumbFactory,
  routingInfoStoreView,
  getSwatchImgPath,
  getProductImagePath,
  getImgPath,
  convertMultipleSizeSkusToAlternatives,
  sumBy,
  getFirstVariant,
  validateQuantityAvailable,
  getProductColorName,
  getTotalQtyAvailable,
  getTotalQtyAvailableBoss,
  getSize,
  getFamilyName,
  getCategoryColorId,
  getPdpUrl,
  getMaxAvailable,
  getMaxAvailableBoss,
  getColorfitsSizesMap,
  parseCategoryId,
  getReviewsCountProduct,
  getCategoryPathMap,
  getCategoryPath2Map,
  getCategory,
  getCategoryId,
  getBaseProduct,
  getProductVariants,
  getFitName,
  getInventoryStatus,
  getSizeName,
  getListPriceProduct,
  getOfferPriceProduct,
  getAlternateSizes,
  getSortedKeys,
  getHasDefaultFit,
  isColorVariant,
  setDefault,
  getDefaultColorAlternateSizes,
  getIsAdditionalStyles,
  getCategoryValue,
  getVariantVisePreOrder,
  getClothingSize,
  getShoesSize,
  getAccessoriesSize,
  getCurrentNavCat,
  processBreadcrumbs,
  getAlternateStyleBasedOnSelectedColor,
  getNewFitList,
  getFitProductMap,
  getFitNameFromProductId,
  getTcpFitMessageStore,
  checkNewFitAttribute,
  getSeoDataForProductVariants,
};
export default processHelperUtil;
